import {
  useEffect,
  useRef,
  useState,
  type ChangeEvent,
  type KeyboardEvent,
} from "react";
import { Input } from "@/components/ui/input";
import { formatMoney, parseMoneyToCents } from "@/lib/format";

export interface MoneyInputProps {
  valueCents: number;
  onChangeCents: (cents: number) => void;
  disabled?: boolean;
  id?: string;
  "aria-label"?: string;
  /** Optional passthrough; invoked after MoneyInput's own Enter/Esc handling. */
  onKeyDown?: (e: KeyboardEvent<HTMLInputElement>) => void;
}

export function MoneyInput({ valueCents, onChangeCents, ...rest }: MoneyInputProps) {
  const [text, setText] = useState(() => formatMoney(valueCents));
  const inputRef = useRef<HTMLInputElement>(null);
  // When set, the next blur reverts to the committed prop value instead of
  // committing the current draft. Used by the Esc-revert key handler.
  const suppressCommit = useRef(false);

  // Sync external value into the buffer, but only when the input is not focused —
  // otherwise an optimistic refresh on the parent would erase the user's edit.
  useEffect(() => {
    if (document.activeElement !== inputRef.current) {
      setText(formatMoney(valueCents));
    }
  }, [valueCents]);

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => setText(e.target.value);

  const handleBlur = () => {
    // Esc requested a revert: restore the committed value, fire no change.
    if (suppressCommit.current) {
      suppressCommit.current = false;
      setText(formatMoney(valueCents));
      return;
    }
    const cents = parseMoneyToCents(text);
    if (cents === null) {
      setText(formatMoney(valueCents));
    } else {
      onChangeCents(cents);
      setText(formatMoney(cents));
    }
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      // Commit the current draft via the existing blur path.
      e.currentTarget.blur();
    } else if (e.key === "Escape") {
      // Revert to the last committed value without committing the draft.
      suppressCommit.current = true;
      e.currentTarget.blur();
    }
    rest.onKeyDown?.(e);
  };

  return (
    <Input
      {...rest}
      ref={inputRef}
      type="text"
      inputMode="decimal"
      value={text}
      onChange={handleChange}
      onBlur={handleBlur}
      onKeyDown={handleKeyDown}
    />
  );
}
